import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NotebookPen, X } from 'lucide-react';

const STORAGE_KEY = 'general_notes';

const FAB_UI = {
  en: {
    title: "General Notes",
    saved: "Saved",
    close: "Close notes",
    helper: "Anything you want to remember across the whole trip — flight numbers, hotel, group leader contact, names to make dua for. Saved automatically on this device.",
    placeholder: "Hotel: ...\nFlight: ...\nGroup leader: ...\nNames to make dua for: ..."
  },
  ar: {
    title: "ملاحظات عامة",
    saved: "تم الحفظ",
    close: "إغلاق الملاحظات",
    helper: "أي شيء تريد تذكره خلال الرحلة كاملة — أرقام الرحلات، الفندق، اسم المسؤول عن المجموعة، أسماء للدعاء لهم. يُحفظ تلقائيًا على هذا الجهاز.",
    placeholder: "الفندق: ...\nالرحلة: ...\nالمسؤول عن المجموعة: ...\nأسماء للدعاء لهم: ..."
  }
};

export default function NotesFAB({ language = 'en' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const t = FAB_UI[language] || FAB_UI.en;
  const isAr = language === 'ar';

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setNotes(saved);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setNotes(val);
    localStorage.setItem(STORAGE_KEY, val);
    setSavedFlash(true);
    clearTimeout(window.__notesSaveTimer);
    window.__notesSaveTimer = setTimeout(() => setSavedFlash(false), 1200);
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.6, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        aria-label="Open general notes"
        data-testid="notes-fab"
        className={`fixed bottom-5 ${isAr ? 'left-5' : 'right-5'} z-40 w-14 h-14 rounded-full bg-secondary text-secondary-foreground shadow-[0_0_20px_rgba(212,175,55,0.5)] border border-white/30 flex items-center justify-center backdrop-blur-md`}
      >
        <NotebookPen className="w-6 h-6" />
        {notes && (
          <span className={`absolute -top-1 ${isAr ? '-left-1' : '-right-1'} w-3.5 h-3.5 bg-primary rounded-full border-2 border-black`} />
        )}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            />

            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 280 }}
              className="fixed bottom-0 left-0 right-0 z-50 max-w-md mx-auto bg-black/90 backdrop-blur-2xl border-t border-l border-r border-white/20 rounded-t-3xl shadow-2xl"
              style={{ maxHeight: '80vh' }}
              dir={isAr ? 'rtl' : 'ltr'}
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <NotebookPen className="w-5 h-5 text-secondary" />
                  <h2 className="text-white font-bold text-lg">{t.title}</h2>
                  <AnimatePresence>
                    {savedFlash && (
                      <motion.span
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        className="text-xs text-primary font-semibold"
                      >
                        {t.saved}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors focus:outline-none"
                  aria-label={t.close}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 pb-6">
                <p className="text-xs text-gray-400 mb-2 leading-relaxed">
                  {t.helper}
                </p>
                <textarea
                  value={notes}
                  onChange={handleChange}
                  placeholder={t.placeholder}
                  className="w-full h-64 bg-black/60 text-white placeholder-gray-500 p-4 rounded-xl border border-white/20 focus:ring-1 focus:ring-secondary focus:border-secondary focus:outline-none resize-none text-sm leading-relaxed font-mono"
                  data-testid="notes-textarea"
                  dir={isAr ? 'rtl' : 'ltr'}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
